import { Link } from "react-router-dom";
import { founderDetails, companyDetails, images, aboutHer } from "../data";
import { getOptimizedImageUrl } from "../lib/utils";
import SEO from "../components/SEO";

export default function About() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Person",
        "name": "Anita Lauridsen",
        "jobTitle": "Strategisk partner i ledelse og arbejdsmiljø",
        "worksFor": {
          "@type": "Organization",
          "name": "Lederskab.nu",
          "url": "https://lederskab.nu"
        },
        "description": "Erfaren leder, sparringspartner og stifter af Lederskab.nu og Stressfri Kommunikation.",
        "image": getOptimizedImageUrl(images.anitaPortrait, "width=800,quality=85,format=auto"),
        "sameAs": [
          "https://www.linkedin.com/company/lederskab-nu/?viewAsMember=true",
          "https://stressfrikommunikation.dk/"
        ]
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://lederskab.nu/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Om Anita",
            "item": "https://lederskab.nu/om-hende"
          }
        ]
      }
    ]
  };

  return (
    <div className="bg-white">
      <SEO
        title="Om Anita Lauridsen | Erfaren Leder & Sparringspartner | Lederskab.nu"
        description="Mød Anita Lauridsen, strategisk partner i ledelse og arbejdsmiljø. Praktisk ledererfaring, nærværende sparring og fokus på værdibaseret lederskab."
        canonicalPath="/om-hende"
        ogImage={getOptimizedImageUrl(images.anitaPortrait, "width=1200,quality=85,format=auto")}
        structuredData={aboutSchema}
      />
      {/* Header Banner */}
      <section className="relative bg-gradient-to-br from-maersk-dark to-navy-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.15),transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-left">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange bg-brand-orange/20 border border-brand-orange/30 px-3 py-1.5 rounded-full">
              Mød Stifteren
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mt-4 mb-6 leading-tight">
              {founderDetails.title}
            </h1>
            <p className="text-gray-300 text-lg sm:text-xl leading-relaxed">
              {founderDetails.subTitle} – med over 20 års praktisk erfaring med mennesker, drift og organisationsudvikling.
            </p>
          </div>
        </div>
      </section>

      {/* Main Presentation Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Left Image */}
            <div className="lg:col-span-5 relative flex flex-col h-full">
              <div className="absolute -inset-4 bg-gradient-to-tr from-maersk-blue/20 to-brand-orange/10 rounded-2xl blur-xl opacity-70" />
              <div className="relative bg-white rounded-2xl shadow-2xl border border-maersk-light overflow-hidden flex-1 flex">
                <img
                  src={getOptimizedImageUrl(images.anitaPortrait, "width=600,quality=85,format=auto")}
                  alt="Anita Lauridsen - Stifter af Lederskab.nu"
                  width="600"
                  height="600"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full min-h-[400px] lg:min-h-0 object-cover"
                />
              </div>
            </div>

            {/* Right Content */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <div className="inline-flex items-center gap-2 bg-brand-orange-light border border-brand-orange/20 px-3 py-1 rounded-full">
                <i className="fa-solid fa-quote-left text-brand-orange text-xs" />
                <span className="text-xs font-bold text-brand-orange">{founderDetails.intro}</span>
              </div>

              <h2 className="font-display text-3xl sm:text-4xl font-bold text-maersk-dark">
                Professionel, nærværende og handlingsorienteret
              </h2>
              
              <p className="text-navy-700 text-lg leading-relaxed">
                {founderDetails.description}
              </p>

              <p className="text-navy-700 text-sm sm:text-base leading-relaxed">
                {founderDetails.approach}
              </p>

              {/* Stressfri Kommunikation Connection Box */}
              <div className="bg-brand-orange-light p-6 sm:p-8 rounded-xl border border-brand-orange/10 space-y-4 mt-6">
                <h4 className="font-display font-bold text-maersk-dark text-lg flex items-center gap-2">
                  <i className="fa-solid fa-heart-pulse text-brand-orange" />
                  Sammenhængen med Stressfri Kommunikation
                </h4>
                <p className="text-navy-700 text-sm leading-relaxed">
                  Udover mit arbejde her i Lederskab.nu, driver jeg også platformen <a href={companyDetails.stressfriUrl} target="_blank" rel="noopener noreferrer" className="text-brand-orange hover:underline font-bold">Stressfri Kommunikation</a>. Her rådgiver jeg virksomheder og private i at opbygge sunde kommunikationsmønstre, forebygge stress og skabe trivsel på arbejdspladsen. De to platforme komplementerer hinanden perfekt, da stærkt lederskab kræver klar og stressfri kommunikation.
                </p>
                <div className="pt-2">
                  <a
                    href={companyDetails.stressfriUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-hover text-white px-5 py-2.5 rounded-md font-bold text-xs tracking-wide transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0"
                  >
                    Besøg Stressfri Kommunikation
                    <i className="fa-solid fa-arrow-up-right-from-square" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience & CV Section */}
      <section className="py-20 bg-navy-50 border-y border-maersk-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left: Key Experience Bullets */}
            <div className="lg:col-span-7 text-left space-y-6">
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-maersk-dark mb-6 border-l-4 border-brand-orange pl-4">
                Erfaring & Baggrund
              </h3>
              <p className="text-navy-700 text-sm sm:text-base leading-relaxed">
                Anita kombinerer dyb strategisk indsigt med praktisk forståelse for hverdagens udfordringer i komplekse driftsorganisationer.
              </p>

              <div className="space-y-4">
                {founderDetails.experienceBullets.map((bullet, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-maersk-blue/5 shadow-xs">
                    <div className="w-6 h-6 rounded-full bg-brand-orange-light flex items-center justify-center text-brand-orange shrink-0 text-xs mt-0.5">
                      <i className="fa-solid fa-check" />
                    </div>
                    <span className="text-sm text-navy-700 font-medium leading-relaxed">{bullet}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Education & CV Timeline */}
            <div className="lg:col-span-5 text-left space-y-8">
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-maersk-dark mb-6 border-l-4 border-maersk-blue pl-4">
                Uddannelse & Certificeringer
              </h3>

              <div className="space-y-6">
                {aboutHer.education.map((edu, idx) => (
                  <div
                    key={idx}
                    className="bg-white p-6 rounded-xl border border-maersk-blue/5 shadow-xs hover:shadow-md transition-all duration-300"
                  >
                    <span className="text-xs font-bold text-maersk-blue bg-maersk-light px-2.5 py-1 rounded-full">
                      {edu.year}
                    </span>
                    <h4 className="font-display font-bold text-maersk-dark text-base mt-3 mb-1">
                      {edu.title}
                    </h4>
                    <p className="text-xs text-navy-700 font-medium">
                      {edu.institution}
                    </p>
                  </div>
                ))}
              </div>

              {/* Professional Values Card */}
              <div className="bg-maersk-dark text-white p-8 rounded-2xl border border-navy-800 space-y-4 shadow-lg">
                <h4 className="font-display font-bold text-lg text-white">
                  Mine kerneværdier i rådgivningen
                </h4>
                <ul className="space-y-3 text-sm text-gray-300">
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-shield-halved text-brand-orange" />
                    <span><strong>Ansvar:</strong> At tage ejerskab for egne handlinger.</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-wind text-brand-orange" />
                    <span><strong>Ro:</strong> At bevare overblikket under pres.</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-users text-brand-orange" />
                    <span><strong>Menneskelig indsigt:</strong> At lede med empati.</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-graduation-cap text-brand-orange" />
                    <span><strong>Faglighed:</strong> Evig læring og dokumenterede metoder.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-display text-3xl font-bold text-maersk-dark">
            Lad os tage en uforpligtende snak
          </h2>
          <p className="text-navy-700 max-w-xl mx-auto text-sm sm:text-base">
            Vil du høre mere om, hvordan Lederskab.nu kan hjælpe dig eller din virksomhed med at styrke lederskabet og samarbejdet?
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
            <Link
              to="/kontakt"
              className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300 shadow-md"
            >
              Book en afklarende samtale <i className="fa-solid fa-calendar-check ml-2" />
            </Link>
            <Link
              to="/kurser"
              className="bg-maersk-light hover:bg-maersk-blue hover:text-white text-maersk-blue px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300"
            >
              Udforsk lederforløbet
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
